/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { stringToNumber } from './timeReducer';
import {
  AM_VALUE,
  getDayPeriodValueFromHour,
  getDayPeriodValueStr,
  getHourValueStr,
  padWithZero,
  PM_VALUE,
  getAmPmStringsForLocale
} from '#utils/UNSAFE_timeUtils';
import type {
  EditableTimeSegmentType,
  OrderedTimeSegmentsArray,
  Time,
  TimeGranularity,
  TimePlaceholders
} from './types';
import type { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { TimeSegment } from './TimeSegment';
import type { ComponentProps } from 'preact';

type InitializerProps = {
  ariaLabels: Record<EditableTimeSegmentType, string>;
  currentValue?: Time;
  granularity: TimeGranularity;
  hasLeadingZeroForHour: boolean;
  locale: BCP47Locale;
  timeMasks: TimePlaceholders;
};

type TimeFieldState = {
  readonly isCommitting: boolean;
  readonly currentTime: Time | undefined;
  readonly isCompleteTime: boolean;
  /**
   * This will be true if any segment has isAutoFilled set to true.
   * This is used to figure out when to render an aria-live region with the full time so
   * a screen reader user will know the time has been auto-filled.
   */
  readonly isTimeAutoFilled: boolean;
  readonly isTimeSelected: boolean;
  readonly isPartialTime: boolean;
  /**
   * An array of the name of each segment type in the order they appear to the user.
   * E.g., ['hour', 'minute, 'dayPeriod'], when the InputTimeMask component shows the hour, minute, and dayPeriod segments in that order.
   * E.g., ['hour', 'minute', 'second'] when the InputTimeMask component shows the hour, minute, and second segments in that order.
   */
  readonly orderedSegments: OrderedTimeSegmentsArray;
  readonly segments: TimeSegmentState[];
};

// Make the state properties readonly because in reducers, the state is replaced, not mutated directly.
type TimeSegmentState = Readonly<
  ComponentProps<typeof TimeSegment> & {
    /** The current digit count for the segment. When the segment is complete or cleared, this will be set to 0.
     * This is useful for figuring out if the segment is complete.
     */
    digitCount?: number;

    /**
     * True if the segment has leading zeros. minute, second, millisecond always have leading zeros, but hour might not;
     * it is based on the locale, and can be overridden with the InputTimeMask's leadingZeroForHour api.
     * Most h23 clocks use a leading zero, and most h12 clocks do not.
     */
    hasLeadingZeros?: boolean;

    /**
     * True if the segment is auto-filled. If any segment has isAutoFilled set to true,
     * then the TimeFieldState's isTimeAutoFilled is set to true, and isTimeAutoFilled is
     * used to figure out when to render an aria-live region with the full time so
     * a screen reader user will know the time has been auto-filled.
     */
    isAutoFilled?: boolean;
  }
>;

const MINUTE_SECOND_MAX = 59;
const MILLISECOND_MAX = 999;

/**
 * Derives the initial state of a time field based on the provided properties.
 *
 * The initial state of the time field can be passed to the timeReducer when the component
 * mounts, and can also be used to reset the state when the value changes programmatically.
 *
 * @param props - An object containing the properties used to derive the initial state.
 * @param props.ariaLabels - An object containing aria labels for the time segments.
 * @param props.timeMasks - An array of time placeholders that define the format of the time.
 * @param {TimeGranularity} props.granularity - Specifies the smallest time unit that is displayed by the component.
 * @param {BCP47Locale} props.locale - The locale used for formatting the time.
 * @param props.currentValue The current value
 * @returns The initial state of the time field that can be passed to the timeReducer.
 **/
const getTimeFieldInitialState = (params: InitializerProps) => {
  const { ariaLabels, currentValue, granularity, hasLeadingZeroForHour, locale, timeMasks } =
    params;
  // Construct an ordered list of the editable segments.
  const ftm = timeMasks.filter(({ type }) => type !== 'literal');
  const orderedSegments = ftm.map(({ type }) => type) as OrderedTimeSegmentsArray;

  const minuteValue = currentValue?.minute;
  const secondValue = currentValue?.second;
  const millisecondValue = currentValue?.millisecond;

  // The timeMasks has a dayPeriod part when the hour is 1-12. You never see a dayPeriod part
  // when the hour is 0-23.
  const hasDayPeriod = timeMasks.some((part) => part.type === 'dayPeriod');
  const hasHour = currentValue?.hour !== undefined;
  const hourValueStr = hasHour
    ? getHourValueStr(currentValue.hour!, hasDayPeriod, hasLeadingZeroForHour)
    : undefined;
  const hourValue = hourValueStr !== undefined ? stringToNumber(hourValueStr) : undefined;
  const dayPeriodValueStr =
    hasDayPeriod && hasHour ? getDayPeriodValueStr(locale, currentValue.hour!) : undefined;
  const dayPeriodValue =
    dayPeriodValueStr !== undefined ? getDayPeriodValueFromHour(currentValue!.hour!) : undefined;

  const minuteValueStr =
    currentValue?.minute === undefined ? undefined : padWithZero(currentValue.minute, 2);
  const secondValueStr =
    currentValue?.second === undefined ? undefined : padWithZero(currentValue.second, 2);
  const millisecondValueStr =
    currentValue?.millisecond === undefined ? undefined : padWithZero(currentValue.millisecond, 3);

  const isBaseTimeComplete =
    hourValue !== undefined &&
    minuteValue !== undefined &&
    (granularity !== 'second' || secondValue !== undefined) &&
    (granularity !== 'millisecond' ||
      (secondValue !== undefined && millisecondValue !== undefined));

  const isCompleteTime = isBaseTimeComplete && (!hasDayPeriod || dayPeriodValue !== undefined);

  // A time is considered a partial time if at least one segment has a value.
  // We need to make sure we look at the granularity, though. The time might have milliseconds only set,
  // but if milliseconds isn't in the orderedSegments, it is not a partial time.
  const isPartialTime =
    hourValue !== undefined ||
    minuteValue !== undefined ||
    ((granularity === 'second' || granularity === 'millisecond') && secondValue !== undefined) ||
    (granularity === 'millisecond' && millisecondValue !== undefined);

  const secondSeg = [
    {
      type: 'second' as const,
      'aria-label': ariaLabels.second,
      'aria-valuemin': 0,
      'aria-valuemax': MINUTE_SECOND_MAX,
      'aria-valuenow': secondValue,
      'aria-valuetext': secondValueStr,
      hasLeadingZeros: true,
      isComplete: secondValue !== undefined,
      isSelected: false,
      text: secondValueStr
    }
  ];

  const millisecondSeg = [
    {
      type: 'millisecond' as const,
      'aria-label': ariaLabels.millisecond,
      'aria-valuemin': 0,
      'aria-valuemax': MILLISECOND_MAX,
      'aria-valuenow': millisecondValue,
      'aria-valuetext': millisecondValueStr,
      hasLeadingZeros: true,
      isComplete: millisecondValueStr !== undefined,
      isSelected: false,
      text: millisecondValueStr
    }
  ];

  const dayPeriodSeg = [
    {
      type: 'dayPeriod' as const,
      amPm: getAmPmStringsForLocale(locale),
      'aria-label': ariaLabels.dayPeriod,
      'aria-valuemin': AM_VALUE,
      'aria-valuemax': PM_VALUE,
      'aria-valuenow': dayPeriodValue,
      'aria-valuetext': dayPeriodValueStr,
      isComplete: dayPeriodValueStr !== undefined,
      isSelected: false,
      text: dayPeriodValueStr
    }
  ];

  // Create state for each editable segment of a time.
  // This does not have to be in order that you see it rendered.
  // The orderedSegments show the order.
  // The timeMasks show the order as well.
  // hour and minute are required segments.

  // hour will be 1-12 if the masks have a 'dayPeriod' part (e.g., 1:00 PM), otherwise the hour is 0-23 (e.g., 13:00).
  const editableSegments = [
    {
      type: 'hour' as const,
      'aria-label': ariaLabels.hour,
      'aria-valuemin': hasDayPeriod ? 1 : 0,
      'aria-valuemax': hasDayPeriod ? 12 : 23,
      'aria-valuenow': hourValue,
      'aria-valuetext': hourValueStr,
      hasLeadingZeros: hasLeadingZeroForHour,
      isComplete: hourValue !== undefined,
      isSelected: false,
      text: hourValueStr
    },
    {
      type: 'minute' as const,
      'aria-label': ariaLabels.minute,
      'aria-valuemin': 0,
      'aria-valuemax': MINUTE_SECOND_MAX,
      'aria-valuenow': minuteValue,
      'aria-valuetext': minuteValueStr,
      hasLeadingZeros: true,
      isComplete: minuteValue !== undefined,
      isSelected: false,
      text: minuteValueStr
    },
    ...(granularity === 'second' || granularity === 'millisecond' ? secondSeg : []),
    ...(granularity === 'millisecond' ? millisecondSeg : []),
    ...(hasDayPeriod ? dayPeriodSeg : [])
  ];

  // Done deriving the data for our initial state,
  // now create the initial state object to pass to our reducer.
  const initialState: TimeFieldState = {
    isCommitting: false,
    currentTime: currentValue,
    isCompleteTime,
    isTimeAutoFilled: false,
    isTimeSelected: false,
    isPartialTime,
    orderedSegments,
    segments: editableSegments
  };
  return initialState;
};

export { getTimeFieldInitialState };
export type { TimeFieldState, TimeSegmentState };
